// 운영자 공지 띠 — 로그인한 앱 화면 맨 위 · 주의는 Banner(warning) · 안내는 같은 모양의 조용한 띠 · 닫으면 공지 id 별로 기억한다.

import { X } from "lucide-react";
import { useSyncExternalStore } from "react";

import type { LiveAnnouncement } from "@buildpy/core/modules/announcement/types";
import Banner from "@buildpy/ui/banner";
import { Button } from "@buildpy/ui/button";
import { cn } from "@/lib/utils";

type KeyStore = Pick<Storage, "getItem" | "setItem">;

export const announcementDismissKey = (id: string): string => `buildpy:announcement-dismissed:${id}`;

const browserStorage = (): KeyStore | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

// 닫은 공지 id — 저장소를 못 쓰면(사생활 모드·차단) 아무것도 닫지 않은 것으로 본다.
export const dismissedIds = (store: KeyStore | null, ids: readonly string[]): string[] => {
  if (!store) return [];
  try {
    return ids.filter((id) => store.getItem(announcementDismissKey(id)) === "1");
  } catch {
    return [];
  }
};

export const rememberDismiss = (store: KeyStore | null, id: string): void => {
  if (!store) return;
  try {
    store.setItem(announcementDismissKey(id), "1");
  } catch {
    // 저장이 막혀도 이번 화면에서는 닫힌다.
  }
};

const listeners = new Set<() => void>();
const sessionDismissed = new Set<string>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  const onStorage = () => listener();
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};

const dismiss = (id: string) => {
  sessionDismissed.add(id);
  rememberDismiss(browserStorage(), id);
  listeners.forEach((l) => l());
};

const AnnouncementBanner = ({ announcements, className }: { announcements: readonly LiveAnnouncement[]; className?: string }) => {
  const ids = announcements.map((a) => a.id);
  // 문자열 스냅샷 — 서버 렌더(닫은 것 없음)와 브라우저 값이 달라도 수화 뒤 맞춘다.
  const snapshot = useSyncExternalStore(
    subscribe,
    () => [...dismissedIds(browserStorage(), ids), ...ids.filter((id) => sessionDismissed.has(id))].join("\n"),
    () => ""
  );
  const hidden = new Set(snapshot === "" ? [] : snapshot.split("\n"));
  const visible = announcements.filter((a) => !hidden.has(a.id));
  if (visible.length === 0) return null;
  return (
    <div className={cn("flex flex-col gap-2", className)} data-testid="announcement-banner">
      {visible.map((a) => {
        const content = (
          <span className="flex items-start gap-2">
            <span className="min-w-0 flex-1 whitespace-pre-line break-words">{a.body}</span>
            <Button aria-label="공지 닫기" data-testid={`announcement-dismiss-${a.id}`} size="icon-xs" variant="ghost" onClick={() => dismiss(a.id)}>
              <X />
            </Button>
          </span>
        );
        return a.level === "warn" ? (
          <Banner key={a.id} tone="warning">
            {content}
          </Banner>
        ) : (
          <p key={a.id} className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground" data-level="info" role="status">
            {content}
          </p>
        );
      })}
    </div>
  );
};

export default AnnouncementBanner;
